<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed } from 'vue';
import { useStore } from 'vuex';
import Avatar from 'next/avatar/Avatar.vue';
import TimeAgo from 'dashboard/components/ui/TimeAgo.vue';
import { getLastMessage } from 'dashboard/helper/conversationHelper';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';

const props = defineProps({
  conversations: { type: Array, default: () => [] },
  labels: { type: Array, default: () => [] },
});

const emit = defineEmits(['openConversation']);
const store = useStore();
const { getPlainText } = useMessageFormatter();

const currentChat = computed(() => store.getters.getSelectedChat || {});

const normalizeLabelTitle = label => {
  if (typeof label === 'string') return label;
  return label?.title || label?.name || '';
};

const labelColor = title => {
  const label = props.labels.find(item => normalizeLabelTitle(item) === title);
  return label?.color || '#64748B';
};

const boardColumns = computed(() => {
  const baseColumns = [
    {
      id: 'untagged',
      title: 'novo contato',
      color: '#94A3B8',
      conversations: [],
    },
    ...props.labels.slice(0, 8).map(label => {
      const title = normalizeLabelTitle(label);
      return {
        id: title,
        title,
        color: label?.color || '#2563EB',
        conversations: [],
      };
    }),
  ].filter(column => column.title);

  const byId = new Map(baseColumns.map(column => [column.id, column]));

  props.conversations.forEach(conversation => {
    const conversationLabels = conversation.labels || [];
    const firstLabel = conversationLabels[0];

    if (!firstLabel) {
      byId.get('untagged')?.conversations.push(conversation);
      return;
    }

    if (!byId.has(firstLabel)) {
      byId.set(firstLabel, {
        id: firstLabel,
        title: firstLabel,
        color: labelColor(firstLabel),
        conversations: [],
      });
    }

    byId.get(firstLabel)?.conversations.push(conversation);
  });

  return [...byId.values()].filter(
    column => column.id === 'untagged' || column.conversations.length
  );
});

const contactForConversation = conversation => {
  const senderId = conversation.meta?.sender?.id;
  if (senderId) return store.getters['contacts/getContact'](senderId) || {};
  return conversation.meta?.sender || {};
};

const previewForConversation = conversation => {
  const message = getLastMessage(conversation);
  if (!message) return 'Sem mensagens';
  if (message.content) return getPlainText(message.content);
  if (message.attachments?.length) return 'Anexo recebido';
  return 'Sem conteúdo';
};

const openConversation = conversation => {
  emit('openConversation', conversation);
};
</script>

<template>
  <section
    class="h-full min-h-0 overflow-hidden bg-[#f7f8fb] dark:bg-n-background"
  >
    <div class="flex h-full min-h-0 gap-4 overflow-x-auto px-5 py-4">
      <div
        v-for="column in boardColumns"
        :key="column.id"
        class="flex h-full min-h-0 w-[300px] shrink-0 flex-col"
      >
        <header class="mb-3 flex items-center justify-between px-1">
          <div class="flex min-w-0 items-center gap-2">
            <span
              class="size-2.5 rounded-full"
              :style="{ backgroundColor: column.color }"
            />
            <h2
              class="truncate text-sm font-semibold"
              :style="{ color: column.color }"
            >
              {{ column.title }}
            </h2>
          </div>
          <span
            class="rounded-full bg-white px-2 py-0.5 text-xs font-medium text-n-slate-10 shadow-sm dark:bg-n-solid-3"
          >
            {{ column.conversations.length }}
          </span>
        </header>

        <div class="min-h-0 flex-1 space-y-2 overflow-y-auto pr-1">
          <button
            v-for="conversation in column.conversations"
            :key="conversation.id"
            type="button"
            class="w-full rounded-2xl border border-n-weak bg-white p-3 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-n-brand/25 hover:shadow-md dark:bg-n-solid-2 dark:hover:bg-n-alpha-2"
            :class="{
              'ring-2 ring-n-brand/15': currentChat.id === conversation.id,
            }"
            @click="openConversation(conversation)"
          >
            <div class="flex items-start gap-3">
              <Avatar
                :name="contactForConversation(conversation).name"
                :src="contactForConversation(conversation).thumbnail"
                :size="28"
                rounded-full
                hide-offline-status
              />
              <div class="min-w-0 flex-1">
                <div class="flex items-center justify-between gap-2">
                  <h3 class="truncate text-sm font-semibold text-n-slate-12">
                    {{ contactForConversation(conversation).name || 'Contato' }}
                  </h3>
                  <TimeAgo
                    class="shrink-0 text-xxs text-n-slate-9"
                    :last-activity-timestamp="conversation.timestamp"
                    :created-at-timestamp="conversation.created_at"
                  />
                </div>
                <p class="mt-1 line-clamp-2 text-sm leading-5 text-n-slate-10">
                  {{ previewForConversation(conversation) }}
                </p>
                <div class="mt-3 flex items-center justify-between">
                  <span class="text-xs text-n-slate-9">
                    #{{ conversation.id }}
                  </span>
                  <span
                    v-if="conversation.unread_count"
                    class="grid min-w-5 place-content-center rounded-full bg-n-brand px-1.5 text-xs font-semibold text-white"
                  >
                    {{
                      conversation.unread_count > 9
                        ? '9+'
                        : conversation.unread_count
                    }}
                  </span>
                </div>
              </div>
            </div>
          </button>
        </div>
      </div>
    </div>
  </section>
</template>
